import React, { useState } from 'react';
import { Modal, Typography, Tag, Button } from 'antd';
import { RobotOutlined, WarningOutlined, DownOutlined, UpOutlined, SettingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useResponsive } from '../../hooks/useResponsive';
import { useAppSelector } from '../../hooks/redux';
import { useIsModuleEnabled } from '../../hooks/useIsModuleEnabled';
import { MODULE_KEYS } from '../../constants/moduleKeys';
import { useGetBoolSettingQuery } from '../../services/settingsApi';
import { useGetDailySummariesQuery } from '../../services/aiChatApi';
import { AiDailySummary } from '../../types/aiChat';
import './dashboardStrip.css';

const { Text } = Typography;

const WEEKDAYS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

/** "2026-07-09" → "miércoles 09/07" — plain calendar date, no tz conversion involved. */
function formatBusinessDate(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  if (!year || !month || !day) return iso;
  const weekday = WEEKDAYS[new Date(year, month - 1, day).getDay()];
  return `${weekday} ${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}`;
}

const SummaryBody: React.FC<{ summary: AiDailySummary; isMobile: boolean }> = ({ summary, isMobile }) => (
  <>
    {summary.anomalies.length > 0 && (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
        {summary.anomalies.map((anomaly, idx) => (
          <div key={idx} className="dash-anomaly" style={{ fontSize: isMobile ? 12 : 13 }}>
            <WarningOutlined className="dash-anomaly__icon" />
            <span>{anomaly}</span>
          </div>
        ))}
      </div>
    )}
    <Text style={{ whiteSpace: 'pre-line', fontSize: isMobile ? 12 : 13, lineHeight: 1.6 }}>
      {summary.summaryText}
    </Text>
  </>
);

export interface AiDailySummaryStatus {
  /** False when the module is off, the digest is off, or nothing is generated yet. */
  visible: boolean;
  latest?: AiDailySummary;
  alertCount: number;
}

/**
 * Drives the "Resumen IA" pill. Same gating the old dashboard card had:
 * turning the digest off in Configuración > Asistente IA retires it (history
 * stays in the DB; it comes back on re-enable).
 */
export function useAiDailySummaryStatus(): AiDailySummaryStatus {
  const { currentRestaurant } = useAppSelector((state) => state.restaurant);
  const { enabled: isAiEnabled } = useIsModuleEnabled(MODULE_KEYS.AsistenteIA);
  const { data: dailySummarySetting } = useGetBoolSettingQuery('AiResumenDiarioActivo', {
    skip: !isAiEnabled,
  });

  const isDigestEnabled = dailySummarySetting?.value ?? false;

  const { data: summaries } = useGetDailySummariesQuery(
    { restaurantId: currentRestaurant?.id ?? '', limit: 7 },
    { skip: !currentRestaurant?.id || !isAiEnabled || !isDigestEnabled }
  );

  if (!isAiEnabled || !isDigestEnabled || !summaries || summaries.length === 0) {
    return { visible: false, alertCount: 0 };
  }

  return {
    visible: true,
    latest: summaries[0],
    alertCount: summaries[0].anomalies.length,
  };
}

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * In-app home of the AI daily digest: latest summary first, previous days
 * collapsible. The push notification is best-effort; this modal always has it.
 */
const AiDailySummaryModal: React.FC<Props> = ({ open, onClose }) => {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const { currentRestaurant } = useAppSelector((state) => state.restaurant);
  const { enabled: isAiEnabled } = useIsModuleEnabled(MODULE_KEYS.AsistenteIA);
  const [expandedDate, setExpandedDate] = useState<string | null>(null);

  const { data: summaries } = useGetDailySummariesQuery(
    { restaurantId: currentRestaurant?.id ?? '', limit: 7 },
    { skip: !currentRestaurant?.id || !isAiEnabled || !open }
  );

  if (!summaries || summaries.length === 0) return null;

  const [latest, ...previous] = summaries;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      destroyOnHidden
      width={isMobile ? '100vw' : 560}
      styles={{ body: { maxHeight: '65vh', overflowY: 'auto' } }}
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <RobotOutlined style={{ marginRight: 8, color: 'var(--color-primary)' }} />
          <span>Resumen IA — {formatBusinessDate(latest.businessDate)}</span>
          {latest.anomalies.length > 0 && (
            <Tag color="warning" style={{ marginLeft: 8 }}>
              {latest.anomalies.length} alerta{latest.anomalies.length > 1 ? 's' : ''}
            </Tag>
          )}
        </div>
      }
      footer={
        <Button icon={<SettingOutlined />} onClick={() => navigate('/ai-settings')}>
          Configurar resumen diario
        </Button>
      }
    >
      <SummaryBody summary={latest} isMobile={isMobile} />

      {previous.length > 0 && (
        <div className="dash-history">
          {previous.map((summary: AiDailySummary) => {
            const isOpen = expandedDate === summary.businessDate;
            return (
              <div key={summary.businessDate} className="dash-history__row">
                <Button
                  type="text"
                  block
                  onClick={() => setExpandedDate(isOpen ? null : summary.businessDate)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '6px 4px',
                    height: 'auto',
                  }}
                >
                  <span style={{ fontSize: isMobile ? 12 : 13 }}>
                    {formatBusinessDate(summary.businessDate)}
                    {summary.anomalies.length > 0 && (
                      <Tag color="warning" style={{ marginLeft: 8, fontSize: 11 }}>
                        {summary.anomalies.length} alerta{summary.anomalies.length > 1 ? 's' : ''}
                      </Tag>
                    )}
                  </span>
                  {isOpen ? <UpOutlined style={{ fontSize: 10 }} /> : <DownOutlined style={{ fontSize: 10 }} />}
                </Button>
                {isOpen && (
                  <div style={{ padding: '4px 4px 12px' }}>
                    <SummaryBody summary={summary} isMobile={isMobile} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
};

export default AiDailySummaryModal;
